<script setup lang="ts">
  /**
   * Detail card — 03-components/13a-detail-card · OSCloud DS 1.1.155
   */
  import type { BfDetailGroup } from './BfDetailCardTypes'

  withDefaults(
    defineProps<{
      groups?: BfDetailGroup[]
      flat?: boolean
    }>(),
    {
      groups: () => [],
      flat: false,
    },
  )
</script>

<template>
  <div class="card" :class="flat ? 'card--flat' : 'card--details'">
    <div v-for="(group, groupIndex) in groups" :key="group.label ?? groupIndex" class="detail-group">
      <span v-if="group.label" class="detail-group__label">{{ group.label }}</span>
      <div class="detail-grid">
        <div v-for="row in group.rows" :key="row.label" class="detail-row" :class="{ 'detail-row--wide': row.wide }">
          <div
            v-if="row.key && $slots[`badge-${row.key}`]"
            class="detail-row__heading"
            :class="{ 'detail-row__heading--align-end': row.badgeAlign === 'end' }"
          >
            <span class="detail-row__label">{{ row.label }}</span>
            <slot :name="`badge-${row.key}`" :row="row" />
          </div>
          <span v-else class="detail-row__label">{{ row.label }}</span>
          <span class="detail-row__value">
            <slot v-if="row.key" :name="`value-${row.key}`" :row="row">{{ row.value }}</slot>
            <template v-else>{{ row.value }}</template>
          </span>
        </div>
      </div>
    </div>
    <slot />
  </div>
</template>

<style scoped lang="scss">
  .card {
    position: relative;
    overflow: hidden;
    border-radius: var(--bf-r-lg);
    background: var(--bf-white);
    box-shadow: var(--bf-shadow-1);

    /* Base .card stays shadow-only; --details is a flatter, read-only data
       container like the DS's other white-surface containers, so it adds the
       same 1px hairline border those carry. */
    &--details {
      --card-pad-block: 18px;
      --card-pad-inline: 22px;
      padding: var(--card-pad-block) var(--card-pad-inline);
      border: 1px solid var(--bf-line);
    }

    &--flat {
      border-radius: 0;
      background: transparent;
      box-shadow: none;
    }
  }

  .detail-group {
    margin-top: 16px;

    &:first-child {
      margin-top: 0;
    }

    &__label {
      display: block;
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
      color: var(--bf-orange-dark);
      margin: 0 0 12px;
    }
  }

  .detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 32px;
  }

  .detail-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    padding: 6px 0;

    /* Wide rows span both columns — anything likely to overflow a half column. */
    &--wide {
      grid-column: 1 / -1;
    }

    &__label {
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
      color: var(--bf-dusky-2);
    }

    &__heading {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 2px var(--bf-sp-2);
      min-width: 0;

      > :deep(.badge) {
        flex: 0 0 auto;
      }

      &--align-end {
        justify-content: space-between;
      }
    }

    &__value {
      font-family: var(--bf-font-body);
      font-size: var(--bf-fs-sm);
      color: var(--bf-deep-blue);
      overflow-wrap: anywhere;
    }
  }

  /* Narrow step: below --bf-bp-sm the box drops to a square 16px inset, so a value's wrap
     point sits the same distance from the border on all four sides. */
  @media (max-width: 600px) {
    .card--details {
      --card-pad-block: 16px;
      --card-pad-inline: 16px;
    }

    .detail-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
